/**
 * NamePill Component
 * A pill-shaped component displaying a user's name with optional avatar, role badge, and close button
 */

import React from "react";
import { HStack, Avatar, Text, Badge, CloseButton, Box } from "@chakra-ui/react";
import { RelationType } from "models/v2/networkRelation";
import CaminoUser from "components/Icon/CaminoUser";
import { useColorModeValue, useColorModeToken } from "../hooks/useColorModeValue";
import { Color } from "../foundation/Color";

// Role display configuration
export const ROLE_CONFIG: Record<RelationType, { label: string; colorScheme: string }> = {
  new_hire: { label: "NEW HIRE", colorScheme: "green" },
  manager: { label: "MANAGER", colorScheme: "purple" },
  buddy: { label: "BUDDY", colorScheme: "teal" },
  mentor: { label: "MENTOR", colorScheme: "orange" },
  people_team_contact: { label: "PEOPLE TEAM", colorScheme: "blue" },
  recruiter: { label: "RECRUITER", colorScheme: "cyan" },
  it_team_contact: { label: "IT TEAM", colorScheme: "gray" },
};

export interface NamePillProps {
  /** The name to display */
  name: string;
  /** URL for the avatar image */
  avatarUrl?: string;
  /** Role badges to display above the pill */
  roles?: RelationType[];
  /** Whether to show the avatar */
  showAvatar?: boolean;
  /** Whether to show the close button */
  showClose?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
  /** Callback when the pill is clicked */
  onClick?: () => void;
  /** Whether the pill is disabled */
  disabled?: boolean;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Props for the Box Container */
  containerProps?: object;
  /** Size variant */
  size?: "sm" | "md";
}

function NamePillComponent({
  name,
  avatarUrl,
  roles = [],
  showAvatar = true,
  showClose = false,
  onClose,
  onClick,
  disabled = false,
  "data-testid": testId,
  containerProps = {},
  size = "md",
}: NamePillProps) {
  const isSmall = size === "sm";
  const hasRoles = roles.length > 0;
  const bgColor = useColorModeToken("#FBF9F6", "#2D2A26");
  const borderColor = useColorModeToken("#E8E2D9", "#4A453D");
  const hoverBorderColor = useColorModeToken("#D4CEC5", "#5C5649");
  const closeHoverBg = useColorModeToken("#F0EBE3", "#3A362F");
  const textColor = useColorModeValue(Color.primitive.gray(700));
  const avatarBgColor = "#B4B4B4";

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onClose) {
      onClose();
    }
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <Box position="relative" display="inline-block" {...containerProps}>
      {/* Role badges positioned at top-left corner */}
      {hasRoles && (
        <HStack position="absolute" top="-2px" left="8px" spacing={0.5} zIndex={1}>
          {roles.map((role) => (
            <Badge
              key={role}
              colorScheme={ROLE_CONFIG[role].colorScheme}
              fontSize={isSmall ? "7px" : "8px"}
              fontWeight="bold"
              px={isSmall ? "2px" : "3px"}
              py="0.5px"
              borderRadius="sm"
              textTransform="uppercase"
              lineHeight="1.5"
            >
              {ROLE_CONFIG[role].label}
            </Badge>
          ))}
        </HStack>
      )}

      {/* Main pill */}
      <HStack
        bg={bgColor}
        border="1px solid"
        borderColor={borderColor}
        color={textColor}
        borderRadius={isSmall ? "8px" : "10px"}
        py={isSmall ? "2px" : "4px"}
        px={isSmall ? "6px" : "8px"}
        gap={isSmall ? "5px" : "8px"}
        spacing={0}
        cursor={onClick ? "pointer" : "default"}
        opacity={disabled ? 0.6 : 1}
        onClick={handleClick}
        data-testid={testId}
        mt={hasRoles ? 2 : 0}
        _hover={onClick && !disabled ? { borderColor: hoverBorderColor } : undefined}
      >
        {showAvatar && (
          <Avatar
            size={isSmall ? "2xs" : "xs"}
            src={avatarUrl}
            icon={<CaminoUser boxSize={isSmall ? 2.5 : 3} />}
            bg={avatarBgColor}
            color="white"
            {...(isSmall ? { boxSize: "18px" } : {})}
          />
        )}

        <Text fontSize={isSmall ? "xs" : "sm"} fontWeight="medium" whiteSpace="nowrap">
          {name}
        </Text>

        {showClose && (
          <CloseButton size="sm" onClick={handleClose} isDisabled={disabled} _hover={{ bg: closeHoverBg }} />
        )}
      </HStack>
    </Box>
  );
}

// Public API
export const NamePill = NamePillComponent;
